import { useEffect, useMemo, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { FaEye, FaRegEdit, FaUserSlash } from 'react-icons/fa';
import { RiShieldUserFill } from 'react-icons/ri';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useExpanded, useFilters, usePagination, useRowSelect, useTable } from 'react-table';
import { toast } from 'react-toastify';
import DefaultColumnFilter from '../../../Components/ReactTable/Filters/DefaultColumnFilter';
import ReactTableRender from '../../../Components/ReactTable/ReactTableRender';
import ToolTip from '../../../Components/ToolTip';
import UserImage from '../../../Components/UserImage';
import { fetchRosters } from '../../../Redux/slices/rosters';
import { clearTable, handleOrganizationRosterListChange } from '../../../Redux/slices/table';
import { fetchWrapper } from '../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../_helpers/_methods';
import Buttons from './Components/Buttons';

function OrganizationRosterTable() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const local = useSelector((state) => state.local.local);
  // const pageNum = useSelector((state) => state.table.pageNum);
  const loading = useSelector((state) => state.table.loading);
  const navigate = useNavigate();
  const rosters = useSelector((state) => state.rosters.rosters);
  const permissions = useSelector((state) => state.user.permissions);
  // const results = useSelector((state) => state.table.results);
  const totalCount = useSelector((state) => state.table.totalCount);

  const [pageNum, setPageNum] = useState(1);
  const gotoPage = (page) => setPageNum(page);
  const previousPage = (page) => setPageNum(page);
  const nextPage = (page) => setPageNum(page);
  const [URL, setURL] = useState(
    '/profile/resource-person/?approved=true&type=department&suspended=false'
  );

  const [suspendedCount, setSuspendedCount] = useState();
  const [pendingCount, setPendingCount] = useState();
  const [filteredRosters, setFilteredRosters] = useState([]);

  useEffect(() => {
    getCounts();
  }, []);
  const getCounts = async () => {
    let res1 = await fetchWrapper.get(
      '/profile/resource-person/?suspended=true&active_role=resource_person'
    );
    setSuspendedCount(res1.data.count);
    let res2 = await fetchWrapper.get(
      '/profile/resource-person/?approved=false&active_role=resource_person'
    );

    setPendingCount(res2.data.count);
  };

  const handleSuspend = async (id) => {
    try {
      await fetchWrapper.get(`/profile/resource-person/${id}/suspend/`);
      toast.success(
        <div className="d-flex">
          <FormattedMessage id="resource_person" /> <FormattedMessage id="suspended_successfully" />
        </div>
      );
      getCounts();
      dispatch(fetchRosters(URL));
    } catch (error) {
      ErrorMessageHandler(error);
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/rosters/organization/${id}/edit`);
  };
  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="sn" />,
        accessor: 'serial_number',
        disableFilters: true,
        width: '8rem'
      },
      {
        Header: <FormattedMessage id="username" />,
        accessor: 'fullname',
        Cell: ({ row: { original }, cell: { value } }) => {
          return (
            <Link to={`/admin/rosters/profile/${original.more_options.id}`}>
              <span className="d-flex align-items-center ">
                <div className="me-1 position-relative">
                  <UserImage
                    avatar={original.image}
                    userName={value}
                    userId={String(original.more_options.id)}
                  />
                  <span className="avatar-profile-iscomplete-badge">
                    {original.warnings && (
                      <ToolTip
                        warnings={original.warnings}
                        isComplete={original.is_profile_updated}
                      />
                    )}
                  </span>
                </div>

                <span className="d-block">
                  {value} <br />
                  {/* <small>({original.office_name})</small> */}
                </span>
                {original.suspended && <Badge bg="danger">Suspended</Badge>}
              </span>
            </Link>
          );
        }
      },
      {
        Header: <FormattedMessage id="address" />,
        accessor: 'address'
      },
      {
        Header: <FormattedMessage id="mobile_number" />,
        accessor: 'phone_number'
      },
      {
        Header: <FormattedMessage id="email" />,
        accessor: 'email'
      },

      {
        Header: <FormattedMessage id="thematic_area" />,
        accessor: 'thematic_area',
        width: 240
      },

      {
        Header: <FormattedMessage id="approved_by" />,
        accessor: 'approved_by'
      },
      {
        Header: <FormattedMessage id="province_priority" />,
        accessor: 'province_priority'
      },
      {
        Header: <FormattedMessage id="resume" />,
        accessor: 'cv_resume',
        disableFilters: true
      },

      {
        Header: <FormattedMessage id="actions" />,
        accessor: 'more_options',
        disableFilters: true,
        Cell: ({ cell: { value } }) => (
          <div className="d-flex justify-content-between align-items-between mx-2">
            <div className="ms-2">
              <Link to={`/admin/rosters/profile/${value.id}`}>
                <Button className="action-button" variant="outline-primary">
                  <FaEye />
                </Button>
              </Link>
            </div>
            {permissions['Can change resource person'] && (
              <>
                <div className="ms-2">
                  {/* <Link to={`/rosters/${value.id}/edit`}> */}
                  <Button
                    className="action-button"
                    onClick={() => handleEdit(value.id)}
                    variant="outline-secondary">
                    <FaRegEdit />
                  </Button>
                  {/* </Link> */}
                </div>

                <div className="ms-2">
                  <Link to={`/admin/rosters/organization_list`}>
                    <Button
                      className="action-button"
                      variant="outline-danger"
                      onClick={() => handleSuspend(value.id)}>
                      <FaUserSlash />
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        )
      }
    ],
    []
  );
  const data = useMemo(() => {
    if (Array.isArray(filteredRosters)) {
      let count = 1;
      return filteredRosters.map((result) => {
        let priority = <div>{result.priorities?.join()}</div>;
        let Thematic_Area =
          local === 'en' ? result.thematic_area?.en.join() : result.thematic_area?.np.join();

        let approved_by = result.approved_by ? result.approved_by?.user : 'N/A';
        let resume = (
          <div>
            {result.resume ? (
              <Button onClick={() => window.open(`${result?.resume}`, '_blank')}>
                <div>
                  <FaEye /> View
                </div>
              </Button>
            ) : (
              'N/A'
            )}
          </div>
        );

        return {
          serial_number: count++,
          fullname: result.fullname,
          email: result.email,
          gender: intl.formatMessage({ id: result.gender || 'p' }),
          phone_number: result.phone,
          address: result.address,
          thematic_area: Thematic_Area,
          approved_by: approved_by,
          province_priority: priority,
          cv_resume: resume,
          more_options: result
        };
      });
    } else {
      return [];
    }
  }, [filteredRosters]);

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,

    // nextPage,
    // previousPage,
    visibleColumns,
    setPageSize,
    state: { pageSize, pageIndex }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn
      // manualFilters: true
    },
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect
  );
  const rosterGenerator = ({ pageSize, pageNum, searchEventParam }) => {
    let params = '';
    if (pageSize) {
      params += `&limit=${pageSize}`;
    }
    if (pageNum) {
      params += `&offset=${(pageNum - 1) * pageSize}&page=${pageNum}`;
    }
    if (searchEventParam) {
      params += searchEventParam;
    }
    return params;
  };

  useEffect(() => {
    let url = '/profile/resource-person/?approved=true&type=department&suspended=false';
    let params = rosterGenerator({
      pageSize: pageSize,
      pageNum: pageNum
    });
    setURL(`${url}${params}`);
  }, [pageSize, pageNum]);

  useEffect(() => {
    dispatch(fetchRosters(URL));
  }, [URL]);

  useEffect(() => {
    setFilteredRosters(rosters);
  }, [rosters]);

  // const pageCountArray = useMemo(
  //   () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
  //   [totalCount, pageSize]
  // );

  useEffect(() => {
    dispatch(handleOrganizationRosterListChange({ index: pageIndex, pageSize: pageSize }));
  }, [pageIndex, pageSize]);

  useEffect(() => {
    return () => {
      dispatch(clearTable());
    };
  }, []);
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount]
  );
  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between">
        <div className="d-flex">
          <h4 className=" text-primary d-inline-block">
            <RiShieldUserFill size={32} className="me-2" />
            <FormattedMessage id="approved_roster" />
            <Badge bg="danger" className="ms-2">
              {totalCount}
            </Badge>
          </h4>

          <ul className="nav mr-auto ms-4 toggle-nav">
            <li className="nav-item p-2">
              <Link className="text-secondary text-center " to="/admin/rosters/individual_list">
                <FormattedMessage id="individual" />
              </Link>
            </li>
            <li className="nav-item bg-primary p-2">
              <Link className="text-light text-center" to="/admin/rosters/organization_list">
                <FormattedMessage id="organization" />
              </Link>
            </li>
          </ul>
        </div>

        <div className="d-flex justify-content-between align-items-end">
          <Buttons pendingCount={pendingCount} suspendedCount={suspendedCount} />
        </div>
      </div>

      <div className="card-body pt-0">
        <ReactTableRender
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          getTableBodyProps={getTableBodyProps}
          page={page}
          prepareRow={prepareRow}
          totalCount={totalCount}
          gotoPage={gotoPage}
          previousPage={previousPage}
          pageCount={pageCount}
          pagination={true}
          pageCountArray={pageCountArray}
          pageNum={pageNum}
          showGoto={true}
          nextPage={nextPage}
          pageIndex={pageIndex}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          loading={loading}
          visibleColumns={visibleColumns}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </div>
    </div>
  );
}

export default OrganizationRosterTable;
